/**
 * The two panes that are one control each: Languages and Agent type.
 *
 * ONE FILE BECAUSE NEITHER HAS ENOUGH TO SAY FOR ITS OWN. They are our team's two decisions
 * about how an agent is delivered - drawn for an internal reader only (see `sections.ts`), refused by the Worker for anybody
 * else, and saved with the page's one bar like every other setting.
 */

import type { SettingsPage } from "../../../api";
import { ChoiceCard, Select, SettingCard, SettingRow, cx } from "../../../ui";
import { useDraft } from "../draft";

/**
 * One language every call starts in, and the others the agent follows a caller into.
 *
 * PILLS FOR THE OTHERS, AS THE PROTOTYPE DRAWS THEM, where the old card had removable chips
 * and an "Add a language" select. The fleet supports about twenty; a row of twenty pills is
 * one glance where a select is twenty clicks. Choosing a main language that was among the
 * others drops it from there: a language cannot be both.
 */
export function LanguagesPane({ languages }: { languages: NonNullable<SettingsPage["agent"]>["languages"] }) {
  const { state, set } = useDraft();
  const { primary, additional } = state.draft.languages;

  return (
    <SettingCard>
      <SettingRow first label="Main language" help="Every call starts in this." htmlFor="main-language">
        <Select
          id="main-language"
          aria-label="Primary language"
          className="w-full max-w-80"
          value={primary}
          onChange={(event) => set("languages", { primary: event.target.value, additional: additional.filter((code) => code !== event.target.value) })}
        >
          {languages.supported.map((language) => (
            <option key={language.code} value={language.code}>
              {language.name}
            </option>
          ))}
        </Select>
      </SettingRow>
      <SettingRow label="Can switch to" help="If the caller speaks one of these, the agent follows.">
        <div className="flex flex-wrap gap-2">
          {languages.supported
            .filter((language) => language.code !== primary)
            .map((language) => {
              const on = additional.includes(language.code);
              return (
                <button
                  key={language.code}
                  type="button"
                  aria-pressed={on}
                  onClick={() =>
                    set("languages", {
                      primary,
                      additional: on ? additional.filter((code) => code !== language.code) : [...additional, language.code],
                    })
                  }
                  className={cx(
                    "h-8.5 rounded-full border px-3.5 text-sm font-medium",
                    on ? "border-accent bg-accent-soft text-ink" : "border-line bg-paper text-ink-soft hover:border-ink",
                  )}
                >
                  {language.name}
                </button>
              );
            })}
        </div>
        <p className="mt-3 text-xs text-ink-faint">
          {languages.source === "fleet" ? "On the fleet's default list until you change it." : "Set for this agent."} Only voices that speak the main language are offered under Voice, so save a change here before choosing one.
        </p>
      </SettingRow>
    </SettingCard>
  );
}

/**
 * Whether the agent is its own code or runs on the shared hosted agent.
 *
 * SAVED WITH THE BAR, AND FIRST. The hosted routes answer 409 for a custom agent, so a switch
 * to hosted goes up before anything that needs it. Moving back keeps everything written,
 * unused, rather than deleting it; the nav changes once the save lands.
 */
export function AgentTypePane() {
  const { state, set } = useDraft();
  const runtime = state.draft.runtime;
  return (
    <div role="radiogroup" aria-label="Agent type" className="grid grid-cols-2 gap-3 max-[720px]:grid-cols-1">
      <ChoiceCard name="agent-type" value="custom" checked={runtime === "custom"} onChange={() => set("runtime", "custom")} title="Custom-built">
        <span className="grid gap-2">
          <span className="text-sm">Our team programs the agent. The owner edits the greeting, hours and tracking.</span>
          <span className="text-ink-faint">Hides Instructions, Tools and Try it.</span>
        </span>
      </ChoiceCard>
      <ChoiceCard name="agent-type" value="hosted" checked={runtime === "hosted"} onChange={() => set("runtime", "hosted")} title="Hosted">
        <span className="grid gap-2">
          <span className="text-sm">Set up entirely in the dashboard. The owner writes the brief and manages tools.</span>
          <span className="text-ink-faint">Shows Instructions, Tools and Try it.</span>
        </span>
      </ChoiceCard>
    </div>
  );
}
